<template>
  <WdsHeading as="div" type="title4">
    <WdsStack justify-space-between>
      {{ $t('Hire') }}
      <WdsButtonGhost :href="path" target="_self">
        <WdsStack justify-center align-center gap-sm>
          {{ $t('Go to workflow details') }}
          <WdsIcon name="arrow-right" :size="16" thick />
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>
  </WdsHeading>
  <WdsDivider class="tw-my-2" />
  <BaseShowMore
    :collapsed-height="230"
    collapsable
    :delay="10"
    start-collapsed
    :expand-text="$t('Show all details')"
    :collapse-text="$t('Hide details')"
  >
    <WdsGrid columns="repeat(2, 1fr)" gap-sm>
      <WdsText class="tw-cursor-pointer" @click="detailsSidebarVisible = true">
        <label>{{ $t("Contractor's name") }}</label>
        <template #subText>
          <span class="c-purple">{{ hireApprovable.worker.name ?? $t('No name') }}</span>
          <br />
          <WdsTag v-if="managedWorker" sm>
            <WdsIcon name="group" :size="12" style="display: inline" />
            {{ $t('Managed worker') }}
          </WdsTag>
        </template>
      </WdsText>
      <WdsText>
        <label>{{ $t('Job title') }}&nbsp;</label>
        <WdsLabel v-if="contractChanges['jobName']" success subtle sm>
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span v-if="contractChanges['jobName']" class="tw-line-through">
            {{ activeContract?.jobName }}
            <br />
          </span>
          {{ latestContract.jobName }}
        </template>
      </WdsText>
      <WdsText>
        <label>{{ $t('Rate') }}&nbsp;</label>
        <WdsLabel v-if="contractChanges['currency'] || contractChanges['rate']" success subtle sm>
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span
            v-if="contractChanges['currency'] || contractChanges['rate']"
            class="tw-line-through"
          >
            <CurrencyDisplay
              v-if="activeContract"
              :converted-currency-code="account?.currency"
              :currency-code="activeContract?.currency"
              :rate="Number(activeContract?.rate)"
              :show-converted-rates-by-default="false"
              show-currency-code
              class="tw-line-through"
            />
            / {{ $t(activeContract?.rateType)?.toLowerCase?.() }}
            <br />
          </span>
          <CurrencyDisplay
            :converted-currency-code="account?.currency"
            :currency-code="latestContract.currency"
            :rate="Number(latestContract.rate)"
            :show-converted-rates-by-default="false"
            show-currency-code
          />
          / {{ $t(latestContract.rateType)?.toLowerCase?.() }}
        </template>
      </WdsText>
      <WdsText>
        <label>{{ $t('Timeframe') }}&nbsp;</label>
        <WdsLabel
          v-if="contractChanges['endDate'] || contractChanges['startDate']"
          success
          subtle
          sm
        >
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span
            v-if="contractChanges['endDate'] || contractChanges['startDate']"
            class="tw-line-through"
          >
            {{ activeDateString }}
            <br />
          </span>
          {{ latestDateString }}
        </template>
      </WdsText>
      <WdsText>
        <label>{{ $t('Purchase order number') }}&nbsp;</label>
        <WdsLabel v-if="contractChanges['purchaseOrderNumber']" success subtle sm>
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span v-if="contractChanges['purchaseOrderNumber']" class="tw-line-through">
            {{ activeContract?.purchaseOrderNumber ?? '----------------' }}
            <br />
          </span>
          {{ latestContract.purchaseOrderNumber ?? '----------------' }}
        </template>
      </WdsText>
      <WdsText>
        <label>{{ $t('Budget') }}</label>
        <template #subText>
          <CurrencyDisplay
            v-if="hireApprovable.job.internalBudget"
            :converted-currency-code="account?.currency"
            :currency-code="hireApprovable.job.currency"
            :rate="Number(hireApprovable.job.internalBudget)"
            :show-converted-rates-by-default="false"
            show-currency-code
          />
          <template v-else>----------------</template>
        </template>
      </WdsText>
    </WdsGrid>
    <WdsText class="tw-my-2">
      <label>{{ $t('Job description') }}&nbsp;</label>
      <WdsLabel v-if="contractChanges['jobDescription']" success subtle sm>
        {{ $t('Updated') }}
      </WdsLabel>
      <template #subText>
        <BaseShowMore
          v-slot="{ expanded }"
          :collapsed-height="38"
          collapsable
          start-collapsed
          :expand-text="$t('Show more')"
          :collapse-text="$t('Hide description')"
        >
          <p class="line-clamp" :class="{ open: expanded }">
            <span v-if="contractChanges['jobDescription']" class="tw-line-through">
              {{ activeContract?.jobDescription }}
              <br />
            </span>
            {{ latestContract.jobDescription }}
          </p>
        </BaseShowMore>
      </template>
    </WdsText>
    <WdsGrid columns="repeat(2, 1fr)" gap-sm>
      <WdsText>
        <label>{{ $t('Location of work') }}&nbsp;</label>
        <WdsLabel v-if="contractChanges['locationPreference']" success subtle sm>
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span
            v-if="contractChanges['locationPreference'] && activeContract"
            class="tw-line-through"
          >
            {{ locationPreferenceLabel(activeContract) }}
            <br />
          </span>
          {{ locationPreferenceLabel(latestContract) }}
        </template>
      </WdsText>
      <WdsText>
        <label>{{ $t('Address') }}&nbsp;</label>
        <WdsLabel v-if="contractChanges['address']" success subtle sm>
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span v-if="contractChanges['address']" class="tw-line-through">
            {{ activeContract?.address }}
            <br />
          </span>
          {{ latestContract.address }}
        </template>
      </WdsText>
      <WdsText>
        <label>{{ hireApprovable.company.hasMultipleBusinessEntities ? $t('Contract legal entity') : $t('Your company name') }}&nbsp;</label>
        <WdsLabel v-if="contractChanges['companyName']" success subtle sm>
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span v-if="contractChanges['companyName']" class="tw-line-through">
            {{ activeContract?.companyName }}
            <br />
          </span>
          {{ latestContract.companyName }}
        </template>
      </WdsText>
      <WdsText>
        <label>{{ hireApprovable.company.hasMultipleBusinessEntities ? $t('Contract legal entity address') : $t('Your company address') }}&nbsp;</label>
        <WdsLabel v-if="contractChanges['companyLocation']" success subtle sm>
          {{ $t('Updated') }}
        </WdsLabel>
        <template #subText>
          <span v-if="contractChanges['companyLocation']" class="tw-line-through">
            {{ activeContract?.companyLocation?.formattedAddress }}
            <br />
          </span>
          {{ latestContract.companyLocation?.formattedAddress }}
        </template>
      </WdsText>
    </WdsGrid>

    <template v-if="managedWorker || hasRecruiter">
      <WdsDivider class="tw-mb-2 tw-mt-1" />
      <WdsGrid columns="repeat(2, 1fr)" gap-sm>
        <WdsText>
          <label>{{ $t('Staffing Agency name') }}</label>
          <template #subText>{{ hireApprovable.recruiter?.name }}</template>
        </WdsText>
        <WdsText>
          <label>{{ $t('Staffing agency contact person') }}</label>
          <template #subText>{{ hireApprovable.recruiter?.owner?.name }}</template>
        </WdsText>
        <template v-if="hasRecruiter">
          <WdsText>
            <label>{{ $t('Ownership period') }}</label>
            <template #subText>{{ hireApprovable.recruiterOwnershipDays }}</template>
          </WdsText>
          <WdsText>
            <label>{{ $t('Staffing agency fee') }}</label>
            <template #subText>
              {{ $t(':fee% (Percent of fees)', { fee: hireApprovable.recruiterFee }) }}
            </template>
          </WdsText>
        </template>
      </WdsGrid>
    </template>

    <div v-if="hireApprovable.job.customFieldValues.length > 0">
      <WdsDivider class="tw-my-2" />
      <WdsHeading as="div" type="title4">
        {{ $t('Custom fields') }}
      </WdsHeading>
      <WdsText
        v-for="field of hireApprovable.job.customFieldValues"
        :key="field.id"
        class="tw-mt-3"
      >
        {{ field.customField.title }}: {{ field.displayValue }}
        <template #subText>
          {{ field.customField.description }}
        </template>
      </WdsText>
    </div>
  </BaseShowMore>
  <WdsDrawer v-model:show="detailsSidebarVisible" position="right" width="420px" padding="0">
    <WorkerDetailPanel
      :worker-id="hireApprovable.worker.id"
      :viewer-account-id="account.id"
      @close="detailsSidebarVisible = false"
    />
  </WdsDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsGrid,
  WdsText,
  WdsHeading,
  WdsDivider,
  WdsDrawer,
  WdsStack,
  WdsButtonGhost,
  WdsIcon,
  WdsTag,
  WdsLabel,
} from '@worksome/wds-components'
import { FragmentType, useFragment } from '@/gql/fragment-masking'
import {
  ApprovableContractFragment,
  HireApprovableFragment,
} from '@/components/Workflows/Approvals/graphql/fragments'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import CurrencyDisplay from '@/components/CurrencyDisplay.vue'
import { ApprovableContractFragmentFragment, LocationPreference } from '@/gql/graphql'
import useDate from '@/composables/useDate'
import BaseShowMore from '@/components/BaseShowMore.vue'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'

const props = defineProps<{ approvable: FragmentType<typeof HireApprovableFragment> }>()

const hireApprovable = useFragment(HireApprovableFragment, props.approvable)

const latestContract = useFragment(ApprovableContractFragment, hireApprovable.latestContract)
const activeContract = useFragment(ApprovableContractFragment, hireApprovable.activeContract)

const contractChanges = computed(() => {
  if (!activeContract) return {}

  const keys = Object.keys(latestContract) as Array<keyof typeof latestContract>
  return keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: latestContract[key] !== activeContract[key],
    }),
    {},
  )
})

const account = currentAccount

const detailsSidebarVisible = ref(false)

const path = laravelNamedRoute('hires.hire.view', { hire: hireApprovable.id, tab: 'approvals' })

const managedWorker = computed(() => !!hireApprovable.recruiterManagesWorkers)
const hasRecruiter = computed(() => !!hireApprovable.recruiter)

function buildDateString(contract: ApprovableContractFragmentFragment) {
  const { date: startDate, isValid: isValidStartDate } = useDate(contract.startDate, 'MMM DD, YYYY')
  const { date: endDate, isValid: isValidEndDate } = useDate(contract.endDate, 'MMM DD, YYYY')
  if (!isValidStartDate.value && !isValidEndDate.value) {
    return '--'
  }

  return `${isValidStartDate.value ? startDate.value : '?'} - ${
    isValidEndDate.value ? endDate.value : '?'
  }`
}

const latestDateString = computed(() => buildDateString(latestContract))
const activeDateString = computed(() => {
  if (activeContract) {
    return buildDateString(activeContract)
  }
  return null
})

function locationPreferenceLabel(contract: ApprovableContractFragmentFragment) {
  switch (contract.locationPreference) {
    case LocationPreference.OnsiteOnly:
      return $t('On-site')
    case LocationPreference.OnsiteSome:
      return $t('Some onsite')
    case LocationPreference.RemoteOnly:
      return $t('Remote')
    default:
      return $t('No preference')
  }
}
</script>

<style lang="scss">
.line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.line-clamp.open {
  height: inherit;
  -webkit-line-clamp: 100000;
}
</style>
